import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomModal from '../../../../Components/Modal/CustomModal';
import BorderedButton from '../../../../Components/Buttons/BorderedButton';
import PrimaryButton from '../../../../Components/Buttons/PrimaryButton';
import CustomDropdown from '../../../../Components/CustomerDetailsModal/CustomDropdown';
import DropdownNoImage from '../../../../Components/DropDown/DropdownNoImage';
import CalendarIcon from '../../../../Components/Icons/CalendarIcon';
import styles from './PayAdjustmentPopup.module.css';

const ADJUSTMENT_TYPES = [
	{ label: 'Pay Adjustment', value: 'Pay Adjustment' },
	{ label: 'Bonus', value: 'Bonus' },
	{ label: 'Service Commission', value: 'Service Commission' },
	{ label: 'Tips', value: 'Tips' },
	{ label: 'Reimbursement', value: 'Reimbursement' },
];

const TIME_OPTIONS = (() => {
	const options = [];
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += 1) {
			const minuteStr = String(minute).padStart(2, '0');
			let hourFormatted;
			let ampm;

			if (hour === 0) {
				hourFormatted = 12;
				ampm = 'AM';
			} else if (hour === 12) {
				hourFormatted = 12;
				ampm = 'PM';
			} else if (hour < 12) {
				hourFormatted = hour;
				ampm = 'AM';
			} else {
				hourFormatted = hour - 12;
				ampm = 'PM';
			}

			options.push({
				label: `${hourFormatted}:${minuteStr} ${ampm}`,
				value: `${String(hour).padStart(2, '0')}:${minuteStr}`,
			});
		}
	}
	return options;
})();

const parseDateValue = (dateStr) => {
	if (!dateStr) return null;

	if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
		const [year, month, day] = dateStr.split('-').map(Number);
		const now = new Date();
		return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), 0);
	}

	const sqlMatch = String(dateStr).match(
		/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
	);
	if (sqlMatch) {
		const [, y, m, d, hh, mm, ss] = sqlMatch;
		return new Date(
			Number(y),
			Number(m) - 1,
			Number(d),
			Number(hh),
			Number(mm),
			Number(ss || 0)
		);
	}

	const d = new Date(dateStr);
	return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTimeForSubmit = (dateObj) => {
	if (!dateObj) return '';
	const year = dateObj.getFullYear();
	const month = String(dateObj.getMonth() + 1).padStart(2, '0');
	const day = String(dateObj.getDate()).padStart(2, '0');
	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');
	const seconds = String(dateObj.getSeconds()).padStart(2, '0');
	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const mergeDatePart = (current, nextDate) => {
	if (!nextDate) return null;
	const base = current || new Date();
	return new Date(
		nextDate.getFullYear(),
		nextDate.getMonth(),
		nextDate.getDate(),
		base.getHours(),
		base.getMinutes(),
		base.getSeconds()
	);
};

const applyTimeToDate = (current, timeValue) => {
	if (!timeValue || !/^\d{2}:\d{2}$/.test(timeValue)) return current;
	const [hours, minutes] = timeValue.split(':').map(Number);
	const base = current || new Date();
	return new Date(
		base.getFullYear(),
		base.getMonth(),
		base.getDate(),
		hours,
		minutes,
		0
	);
};

const getTimeValueFromDate = (dateObj) => {
	if (!dateObj) return null;
	return `${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
};

const to12HourLabel = (value) => {
	if (!value || !/^\d{2}:\d{2}$/.test(value)) return '';
	const [hour24, minute] = value.split(':').map(Number);
	const ampm = hour24 >= 12 ? 'PM' : 'AM';
	const hour12 = hour24 % 12 || 12;
	return `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
};

const parseInputTo24Hour = (value) => {
	const trimmed = (value || '').trim().toLowerCase();
	if (!trimmed) return null;

	const ampmMatch = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/i);
	if (ampmMatch) {
		let hours = Number(ampmMatch[1]);
		const minutes = Number(ampmMatch[2] || '0');
		const meridiem = ampmMatch[3].toLowerCase();

		if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
		if (meridiem === 'pm' && hours !== 12) hours += 12;
		if (meridiem === 'am' && hours === 12) hours = 0;

		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	}

	const hhmmMatch = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))$/);
	if (hhmmMatch) {
		const hours = Number(hhmmMatch[1]);
		const minutes = Number(hhmmMatch[2]);
		if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	}

	const fourDigitsMatch = trimmed.match(/^(\d{1,2})(\d{2})$/);
	if (fourDigitsMatch) {
		const hours = Number(fourDigitsMatch[1]);
		const minutes = Number(fourDigitsMatch[2]);
		if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	}

	return null;
};

const formatTimeInputForTyping = (value, shouldAutoAppendMeridiem = true) => {
	const rawValue = (value || '').toUpperCase();
	const digits = rawValue.replace(/\D/g, '').slice(0, 4);
	const letters = rawValue.replace(/[^APM]/g, '');

	if (!digits) return '';

	let formatted = digits.slice(0, Math.min(2, digits.length));
	if (digits.length >= 2) {
		formatted = `${digits.slice(0, 2)}:`;
	}
	if (digits.length > 2) {
		formatted = `${digits.slice(0, 2)}:${digits.slice(2)}`;
	}

	if (digits.length === 4 && shouldAutoAppendMeridiem) {
		const meridiem = letters.startsWith('P') ? 'PM' : 'AM';
		formatted = `${formatted} ${meridiem}`;
	}

	return formatted;
};

const DateReadOnlyInput = React.forwardRef(({ value, onClick, className }, ref) => (
	<input
		ref={ref}
		value={value}
		onClick={onClick}
		readOnly
		className={`${styles.dateInput} ${className || ''}`}
	/>
));
DateReadOnlyInput.displayName = 'DateReadOnlyInput';

const formatAmountToTwoDecimals = (value) =>
	Math.round(parseFloat(value) * 100) / 100;

const isValidAmountInput = (value) =>
	value === '' || value === '-' || /^-?\d*(\.\d{0,2})?$/.test(value);

const PayAdjustmentPopup = ({
	show,
	onClose,
	onSubmit,
	date,
	mode = 'add',
	adjustment = null,
	isProcessing = false,
}) => {
	const isEditMode = mode === 'edit';
	const [type, setType] = useState('Pay Adjustment');
	const [selectedDate, setSelectedDate] = useState(null);
	const [showCalendar, setShowCalendar] = useState(false);
	const [selectedTime, setSelectedTime] = useState(null);
	const [timeInput, setTimeInput] = useState('');
	const [amount, setAmount] = useState('');
	const [description, setDescription] = useState('');
	const [errors, setErrors] = useState({});
	const timeDropdownRef = useRef(null);

	const selectedTimeLabel = useMemo(() => {
		if (!selectedTime) return '';
		return TIME_OPTIONS.find((opt) => opt.value === selectedTime)?.label || to12HourLabel(selectedTime);
	}, [selectedTime]);

	useEffect(() => {
		if (!show) return;

		const initialDate = isEditMode && adjustment?.date ? adjustment.date : date;

		if (isEditMode && adjustment) {
			setType(adjustment.type || 'Pay Adjustment');
			const parsedAmount = parseFloat(adjustment.amount);
			setAmount(Number.isNaN(parsedAmount) ? '' : parsedAmount.toFixed(2));
			setDescription(adjustment.description || '');
		} else {
			setType('Pay Adjustment');
			setAmount('');
			setDescription('');
		}

		const parsedDate = parseDateValue(initialDate);
		const timeValue = getTimeValueFromDate(parsedDate);
		setSelectedDate(parsedDate);
		setSelectedTime(timeValue);
		setTimeInput(timeValue ? to12HourLabel(timeValue) : '');
		setShowCalendar(false);
		setErrors({});
	}, [show, date, isEditMode, adjustment]);

	const validate = () => {
		const nextErrors = {};
		const resolvedTime = selectedTime || parseInputTo24Hour(timeInput);

		if (!selectedDate) {
			nextErrors.date = 'Date is required';
		}
		if (!resolvedTime) {
			nextErrors.time = 'Time is required';
		}
		if (!type) {
			nextErrors.type = 'Type is required';
		}
		const parsedAmount = parseFloat(amount);
		if (amount === '' || Number.isNaN(parsedAmount)) {
			nextErrors.amount = 'Enter a valid amount';
		}
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const buildSubmitPayload = () => {
		const resolvedTime = selectedTime || parseInputTo24Hour(timeInput);
		const dateWithTime = applyTimeToDate(selectedDate, resolvedTime);
		return {
			date: formatDateTimeForSubmit(dateWithTime),
			type,
			amount: formatAmountToTwoDecimals(amount),
			description: description.trim(),
		};
	};

	const handleSubmit = () => {
		if (!validate()) return;

		const resolvedTime = selectedTime || parseInputTo24Hour(timeInput);
		if (resolvedTime && resolvedTime !== selectedTime) {
			setSelectedTime(resolvedTime);
			setTimeInput(to12HourLabel(resolvedTime));
			setSelectedDate((prev) => applyTimeToDate(prev, resolvedTime));
		}

		const payload = buildSubmitPayload();

		if (isEditMode) {
			onSubmit({
				id: adjustment?.id,
				...payload,
			});
			return;
		}

		onSubmit(payload);
	};

	const handleDatePartChange = (newDate) => {
		setSelectedDate((prev) => {
			const withDate = mergeDatePart(prev, newDate);
			return selectedTime ? applyTimeToDate(withDate, selectedTime) : withDate;
		});
		setShowCalendar(false);
		setErrors((prev) => ({ ...prev, date: '' }));
	};

	const handleTimeSelect = (selectedOption) => {
		const nextTime = selectedOption?.value || null;
		setSelectedTime(nextTime);
		setTimeInput(nextTime ? to12HourLabel(nextTime) : '');
		setSelectedDate((prev) => applyTimeToDate(prev, nextTime));
		setErrors((prev) => ({ ...prev, time: '' }));
	};

	const handleTimeInputChange = (event) => {
		const isDeleting = event?.nativeEvent?.inputType?.startsWith('delete');
		const nextValue = formatTimeInputForTyping(event.target.value, !isDeleting);
		setTimeInput(nextValue);
		setSelectedTime(null);
		const parsedTypedValue = parseInputTo24Hour(nextValue);
		setErrors((prev) => ({
			...prev,
			time: Boolean(nextValue) && nextValue.includes(':') && !parsedTypedValue
				? 'Enter a valid time'
				: '',
		}));
	};

	const commitTypedTime = (value) => {
		if (!value) return;
		const parsedValue = parseInputTo24Hour(value);
		if (!parsedValue) {
			setErrors((prev) => ({ ...prev, time: 'Enter a valid time' }));
			return;
		}
		setSelectedTime(parsedValue);
		setTimeInput(to12HourLabel(parsedValue));
		setSelectedDate((prev) => applyTimeToDate(prev, parsedValue));
		setErrors((prev) => ({ ...prev, time: '' }));
	};

	const handleTimeInputKeyDown = (event) => {
		if (event.key.length === 1 && /[a-z]/i.test(event.key)) {
			const hasMeridiem = /\s(AM|PM)$/i.test(timeInput || '');
			if (hasMeridiem) {
				event.preventDefault();
				const toggled = timeInput.trim().toUpperCase().endsWith('AM')
					? timeInput.replace(/\sAM$/i, ' PM')
					: timeInput.replace(/\sPM$/i, ' AM');
				setTimeInput(toggled);
				const parsed = parseInputTo24Hour(toggled);
				if (parsed) {
					setSelectedTime(parsed);
					setSelectedDate((prev) => applyTimeToDate(prev, parsed));
					setErrors((prev) => ({ ...prev, time: '' }));
				}
				return;
			}
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			commitTypedTime(timeInput);
		}
	};

	const openTimeDropdown = () => {
		const dropdownToggle = timeDropdownRef?.current?.querySelector('.dropdownToggle');
		if (dropdownToggle) {
			dropdownToggle.click();
		}
	};

	const renderDateTimeFields = () => (
		<div className={styles.dateTimeRow}>
			<div className={styles.dateTimeField}>
				<label className={styles.formLabel}>Date*</label>
				<div className={styles.dateFieldRow}>
					<div
						className={styles.datePickerWrapper}
						onClick={(e) => {
							if (!e.target.closest('.react-datepicker')) {
								setShowCalendar((prev) => !prev);
							}
						}}
					>
						<DatePicker
							locale="en"
							selected={selectedDate}
							onChange={handleDatePartChange}
							dateFormat="dd/MM/yyyy"
							open={showCalendar}
							onClickOutside={() => setShowCalendar(false)}
							calendarClassName={styles.customDatePicker}
							popperClassName={styles.datePickerPopper}
							popperPlacement="bottom-start"
							customInput={<DateReadOnlyInput />}
						/>
					</div>
					<button
						type="button"
						className={styles.calendarButton}
						onClick={(e) => {
							e.stopPropagation();
							setShowCalendar((prev) => !prev);
						}}
						aria-label="Open calendar"
					>
						<CalendarIcon size={18} strokeColor="#767676" />
					</button>
				</div>
				{errors.date && <span className={styles.errorText}>{errors.date}</span>}
			</div>

			<div className={styles.dateTimeField}>
				<label className={styles.formLabel}>Time*</label>
				<div className={styles.timeDropdownWrapper} ref={timeDropdownRef}>
					<DropdownNoImage
						options={TIME_OPTIONS}
						selectedLabel={selectedTimeLabel}
						onSelect={handleTimeSelect}
						dropdownClass={`${styles.hiddenTimeDropdown} ${styles.timeDropdown}`}
						dropdownMenuClass={`${styles.dropdownMenu} ${styles.timeDropdownMenu}`}
						placeholder="Select time"
						placeholderClass={styles.placeholder}
						labelText=""
						enableSearch
						searchPlaceholder="Search time..."
					/>
					<div className={`${styles.timeInputWrapper} ${errors.time ? styles.timeInputError : ''}`}>
						<input
							type="text"
							value={timeInput}
							onChange={handleTimeInputChange}
							onKeyDown={handleTimeInputKeyDown}
							onBlur={() => commitTypedTime(timeInput)}
							className={styles.timeInput}
							placeholder="--:--"
						/>
						<button
							type="button"
							className={styles.timeInputClockIcon}
							aria-label="Open time options"
							onClick={openTimeDropdown}
						>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
								<path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							</svg>
						</button>
					</div>
				</div>
				{errors.time && <span className={styles.errorText}>{errors.time}</span>}
			</div>
		</div>
	);

	return (
		<CustomModal
			show={show}
			onHide={onClose}
			customClassBody={styles.modalBodyReset}
			customClassModal={styles.modalShell}
			hideCloseButton={true}
		>
			<div className={styles.popup}>
				<button
					type="button"
					className={styles.localCloseButton}
					onClick={onClose}
					aria-label="Close"
				>
					&times;
				</button>

				<div className={styles.header}>
					<h2>{isEditMode ? 'Edit Pay Adjustment' : 'Add Pay Adjustment'}</h2>
				</div>

				<div className={styles.body}>
					<div className={styles.formGroup}>
						{renderDateTimeFields()}
					</div>

					<div className={styles.formGroup}>
						<label className={styles.formLabel}>Type*</label>
						<CustomDropdown
							options={ADJUSTMENT_TYPES}
							value={type}
							onChange={(optionValue) => {
								setType(optionValue);
								setErrors((prev) => ({ ...prev, type: '' }));
							}}
							placeholder="Select type"
							disableSearch
						/>
						{errors.type && <span className={styles.errorText}>{errors.type}</span>}
					</div>

					<div className={styles.formGroup}>
						<label className={styles.formLabel}>Amount*</label>
						<input
							type="text"
							inputMode="decimal"
							value={amount}
							onChange={(e) => {
								const value = e.target.value;
								if (isValidAmountInput(value)) {
									setAmount(value);
									setErrors((prev) => ({ ...prev, amount: '' }));
								}
							}}
							onBlur={() => {
								if (amount === '' || amount === '-') return;
								const parsedAmount = parseFloat(amount);
								if (!Number.isNaN(parsedAmount)) {
									setAmount(parsedAmount.toFixed(2));
								}
							}}
							className={styles.formInput}
							placeholder="Enter amount (e.g. 30 or -30)"
						/>
						{errors.amount && <span className={styles.errorText}>{errors.amount}</span>}
					</div>

					<div className={styles.formGroup}>
						<label className={styles.formLabel}>Description (optional)</label>
						<textarea
							value={description}
							onChange={(e) => setDescription(e.target.value.trimStart())}
							className={styles.textarea}
							rows={3}
							placeholder="Write a description (e.g. Bonus)"
						/>
					</div>
				</div>

				<div className={styles.separator} />

				<div className={styles.footer}>
					<BorderedButton
						text="Cancel"
						onClick={onClose}
						className={styles.cancelButton}
						disabled={isProcessing}
					/>
					<PrimaryButton
						text={
							isProcessing
								? (isEditMode ? 'Updating...' : 'Adding...')
								: (isEditMode ? 'Update' : 'Add')
						}
						onClick={handleSubmit}
						disabled={isProcessing}
						className={styles.confirmButton}
					/>
				</div>
			</div>
		</CustomModal>
	);
};

export default PayAdjustmentPopup;
